import {
  AddCircleOutline,
  ArrowDownward,
  ArrowUpward,
  ChangeCircleOutlined,
  ExpandMore,
  RemoveCircleOutline,
} from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  Divider,
  Grid,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { isObject } from '@mui/x-data-grid/internals';
import { ArrowRightIcon } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import { useEffect, useMemo, useState } from 'react';
import {
  getVoyageScheduleHistory,
  getVoyageHistoryDates,
} from '../../../../services/VoyageRepository';
import IconButtonDatePicker from '../../../../../../shared/components/pickers/IconButtonDatePicker';
import MBModal from '../../../../../../shared/components/modals/MBModal';
import { Market } from '../../../../types/schedule.types';

interface FieldChange {
  field: string;
  value?: string | number | boolean | null;
  initialValue?: string | number | boolean | null;
  previousValue?: string | number | boolean | null;
  currentValue?: string | number | boolean | null;
}

type ShipmentChangeType = 'ADDED' | 'UPDATED' | 'DELETED';

interface ShipmentChange {
  market: Market;
  marketSnapshot?: Market;
  createdAt: string;
  changeType: ShipmentChangeType;
  fieldChanges: FieldChange[];
}

interface VoyageHistoryData {
  voyageId: string;
  date: string;
  voyageFieldChanges: FieldChange[];
  shipmentsCreated: ShipmentChange[];
  shipmentsUpdated: ShipmentChange[];
  shipmentsDeleted: ShipmentChange[];
}

interface VoyageHistoryModalProps {
  open: boolean;
  onClose: () => void;
  selectedVoyage?: any;
  handleShipmentHistoryClick: (shipment: any) => void;
}

const getCellDisplayValue = (obj: any, field: string): string => {
  if (!isObject(obj)) {
    return obj !== null && obj !== undefined ? String(obj) : '-';
  }

  switch (field) {
    case 'market':
      return obj.name || '-';

    case 'departurePort':
    case 'destinationPort':
      return `${obj.unLocode || '??'} ${obj.name || ''}`;

    case 'departureCountry':
      return obj.name || '-';

    case 'destinationCountry':
      return obj.name || '-';

    case 'carrier':
      return obj.supplierName || '-';

    case 'portOperator':
      return obj.supplierName || '-';

    case 'lastUpdatedBy':
      return obj.emea || '-';

    default:
      return JSON.stringify(obj);
  }
};

const mapLabelToField = (label: string): string =>
  ({
    adjustedPORequest: 'Adjusted PO Request',
    market: 'Market',
    voyageNumber: 'Voyage Number',
    departurePort: 'Departure Port',
    destinationPort: 'Destination Port',
    initialEtd: 'Initial ETD',
    initialEta: 'Initial ETA',
    actualEtd: 'Actual ETD',
    actualEta: 'Actual ETA',
    version: 'Version',
    generalRemarks: 'General Remark',
    slotConfirmed: 'Slot Confirmed',
    carrier: 'Carrier',
    portOperator: 'Port Operator',
    destinationCountry: 'Destination Country',
    departureCountry: 'Departure Country',
    imo: 'IMO',
    effectiveShipped: 'Effective Shipped',
    adjustedPoRequest: 'Adjusted PO Request',
    finalAgreement: 'Final Agreement',
    firstShippingLineOffer: 'First Shipping Line Offer',
    lastUpdatedBy: 'Last Updated By',
  })[label] || label;

const isDateField = (fieldName: string): boolean => {
  const dateFields = ['Initial ETD', 'Initial ETA', 'Actual ETD', 'Actual ETA'];
  return dateFields.includes(fieldName);
};

const formatTableDate = (date: string) => {
  return dayjs(date).format('DD-MMM-YYYY');
};

const formatDisplayValue = (value: string, fieldName: string): string => {
  if (!value || value === '-') return '-';

  if (isDateField(fieldName)) {
    try {
      return formatTableDate(value);
    } catch {
      return value;
    }
  }

  return value;
};

const getShipmentsDisplayData = (shipments: ShipmentChange[]) => {
  const grouped: Record<string, any[]> = {};

  shipments.forEach((shipment, shipmentIdx) => {
    const {
      name = 'Unknown',
      id,
      country = { countryCode: 'Unknown' },
    } = shipment.market;
    const countryCode = country?.countryCode || 'Unknown';
    const groupKey = `${countryCode}-${id}-${name}-${shipmentIdx}`;

    grouped[groupKey] = shipment.fieldChanges.map((change, idx) => {
      const isUpdated = shipment.changeType === 'UPDATED';
      return {
        id: `${groupKey}-${idx}`,
        market: shipment.market,
        fields: mapLabelToField(change.field),
        createdAt: shipment.createdAt,
        initialData: isUpdated
          ? getCellDisplayValue(change.initialValue, change.field)
          : null,
        historyData: isUpdated
          ? getCellDisplayValue(change.previousValue, change.field)
          : null,
        latestData: getCellDisplayValue(
          change.currentValue ?? change.value,
          change.field
        ),
        changeType: shipment.changeType,
      };
    });
  });

  return grouped;
};

const getShipmentColumns = (
  changeType: ShipmentChangeType,
  startDate: string,
  theme: any
): GridColDef[] => {
  if (changeType !== 'UPDATED') {
    return [
      { field: 'fields', headerName: 'Field', flex: 1 },
      { field: 'latestData', headerName: 'Value', flex: 1 },
    ];
  }

  return [
    { field: 'fields', headerName: 'Field', flex: 1 },
    { field: 'initialData', headerName: 'Initial', flex: 1 },
    {
      field: 'historyData',
      headerName: `${formatTableDate(startDate)}`,
      flex: 1,
      renderCell: ({ row, value }) => {
        const formattedValue = formatDisplayValue(value, row.fields);
        return <span style={{ color: 'red' }}>{formattedValue}</span>;
      },
    },
    {
      field: 'latestData',
      headerName: 'Latest',
      flex: 1,
      renderCell: ({ row, value }) => {
        const formattedValue = formatDisplayValue(value, row.fields);
        const prev = Number(row.historyData);
        const latest = Number(value);
        if (!isNaN(prev) && !isNaN(latest)) {
          const Icon =
            latest > prev
              ? ArrowUpward
              : latest < prev
                ? ArrowDownward
                : undefined;
          const iconColor =
            latest > prev
              ? theme.palette.success.main
              : latest < prev
                ? theme.palette.error.main
                : undefined;
          return (
            <Stack direction='row' alignItems='center' spacing={0.5}>
              {Icon && <Icon sx={{ color: iconColor, fontSize: 18 }} />}
              <span style={{ color: 'green' }}>{formattedValue}</span>
            </Stack>
          );
        }
        return <span style={{ color: 'green' }}>{formattedValue}</span>;
      },
    },
  ];
};

// Components

const ChangesDataGrid = ({
  rows,
  columns,
  onRowClick,
}: {
  rows: any[];
  columns: GridColDef[];
  onRowClick?: (params: any) => void;
}) => (
  <Box sx={{ overflow: 'hidden', boxShadow: 1 }}>
    <DataGridPro
      density='compact'
      hideFooter
      disableColumnMenu
      columns={columns}
      rows={rows}
      onRowClick={onRowClick}
      sx={{
        '& .MuiDataGrid-columnHeaders': {
          position: 'sticky',
          top: 0,
          zIndex: 20,
          bgcolor: 'background.paper',
        },
        '& .MuiDataGrid-row:hover': {
          bgcolor: 'action.hover',
          cursor: onRowClick ? 'pointer' : 'default',
        },
      }}
    />
  </Box>
);

const ShipmentChangeSection = ({
  title,
  countryCode,
  marketId,
  createdAt,
  changeType,
  rows,
  columns,
  onRowClick,
}: {
  title: string;
  countryCode?: string;
  marketId?: string;
  createdAt?: string;
  changeType?: ShipmentChangeType;
  rows: any[];
  columns: GridColDef[];
  onRowClick?: (params: any) => void;
}) => (
  <Box sx={{ overflow: 'hidden', boxShadow: 1 }}>
    <Stack
      direction='row'
      alignItems='center'
      p={1.5}
      borderBottom={(theme) => `1px solid ${theme.palette.divider}`}
    >
      <Stack width={'100%'} direction='column' alignItems='start'>
        <Stack direction='row' alignItems='center'>
          <Box>
            {countryCode && (
              <img
                src={`https://flagcdn.com/w20/${countryCode.toLowerCase()}.png`}
                alt={countryCode}
                width={20}
                height={15}
                style={{ marginLeft: 5 }}
              />
            )}
          </Box>
          <Box position={'relative'}>
            <Typography variant='subtitle1' sx={{ ml: 1 }}>
              {title} - {countryCode}
            </Typography>
            <Box position='absolute' top={-8} right={-40}>
              <Chip label={marketId} size='small' />
            </Box>
          </Box>
        </Stack>
        <Typography variant='body2' color={'text.secondary'}>
          {createdAt && `${changeType} — ${dayjs(createdAt).format('LLL')}`}
        </Typography>
      </Stack>
    </Stack>
    <ChangesDataGrid rows={rows} columns={columns} onRowClick={onRowClick} />
  </Box>
);

const VoyageHeader = ({
  selectedVoyage,
  startDate,
  setStartDate,
}: {
  selectedVoyage?: any;
  startDate: string;
  setStartDate: (date: string) => void;
}) => {
  const [historyDates, setHistoryDates] = useState<string[]>([]);

  useEffect(() => {
    const fetchDates = async () => {
      if (!selectedVoyage?.id) {
        setHistoryDates([]);
        return;
      }
      try {
        const dates = await getVoyageHistoryDates(selectedVoyage.id);
        const normalized = Array.isArray(dates)
          ? dates.filter(Boolean).map((d) => dayjs(d).format('YYYY-MM-DD'))
          : [];
        setHistoryDates(normalized);

        // Auto-select the latest available history date if current startDate is not in the list
        if (normalized.length) {
          const latest = normalized
            .slice()
            .sort((a, b) => dayjs(a).valueOf() - dayjs(b).valueOf())
            .pop();
          if (latest && !normalized.includes(startDate)) {
            setStartDate(latest);
          }
        }
      } catch (e) {
        setHistoryDates([]);
      }
    };
    fetchDates();
  }, [selectedVoyage?.id, startDate, setStartDate]);

  return (
    <Box
      sx={{
        display: 'flex',
        justifyItems: 'center',
        justifyContent: 'space-between',
        alignItems: 'center',
        bgcolor: 'background.paper',
        p: 2,
        position: 'sticky',
        top: -20,
        zIndex: 30,
        borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
      }}
    >
      <Box>
        <Typography variant='h6' fontWeight='bold'>
          {selectedVoyage?.voyageNumber || '-'} (
          {selectedVoyage?.ship?.name || '-'})
        </Typography>
        <Typography variant='body2' color='text.secondary'>
          {selectedVoyage?.departurePort.unLocode || '??'}{' '}
          {selectedVoyage?.departurePort.name || ''} →{' '}
          {selectedVoyage?.destinationPort.unLocode || '??'}{' '}
          {selectedVoyage?.destinationPort.name || ''}
        </Typography>
      </Box>
      <Stack
        direction='row'
        spacing={1.5}
        alignItems='center'
        sx={{
          bgcolor: 'background.default',
          borderRadius: 1,
          px: 2,
          py: 1,
        }}
      >
        <Typography
          variant='body2'
          color='text.secondary'
          fontWeight={500}
          sx={{ whiteSpace: 'nowrap' }}
        >
          Compare with:
        </Typography>
        <IconButtonDatePicker
          value={dayjs(startDate)}
          highlightedDates={historyDates}
          onChange={(newValue: dayjs.Dayjs | null) => {
            setStartDate(newValue ? dayjs(newValue).format('YYYY-MM-DD') : '');
          }}
        />
      </Stack>
    </Box>
  );
};

const useSectionOpen = (key: string, defaultValue = true) => {
  const [open, setOpen] = useState<boolean>(() => {
    const stored = localStorage.getItem(key);
    return stored === null ? defaultValue : stored === 'true';
  });
  useEffect(() => {
    localStorage.setItem(key, String(open));
  }, [key, open]);
  return [open, setOpen] as const;
};

const VoyageHistoryModal = ({
  open,
  onClose,
  selectedVoyage,
  handleShipmentHistoryClick,
}: VoyageHistoryModalProps) => {
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [voyageHistory, setVoyageHistory] = useState<VoyageHistoryData | null>(
    null
  );
  const [startDate, setStartDate] = useState(dayjs().format('YYYY-MM-DD'));

  const [isUpdatedVoyageSectionOpen, setIsUpdatedVoyageSectionOpen] =
    useSectionOpen('is-updated-voyage-history-section-open');
  const [isUpdatedShipmentSectionOpen, setIsUpdatedShipmentSectionOpen] =
    useSectionOpen('is-updated-shipment-history-section-open');
  const [isAddedShipmentSectionOpen, setIsAddedShipmentSectionOpen] =
    useSectionOpen('is-added-shipment-history-section-open');
  const [isRemovedShipmentSectionOpen, setIsRemovedShipmentSectionOpen] =
    useSectionOpen('is-removed-shipment-history-section-open');

  useEffect(() => {
    if (!selectedVoyage?.id) return;

    const fetchHistory = async () => {
      setLoading(true);
      try {
        const data = await getVoyageScheduleHistory(
          selectedVoyage.id,
          startDate
        );
        setVoyageHistory(data);
      } catch {
        setError('Failed to load voyage history');
      } finally {
        setLoading(false);
      }
    };

    fetchHistory();
  }, [selectedVoyage?.id, startDate, open]);

  const voyageColumns: GridColDef[] = useMemo(
    () => [
      { field: 'fields', headerName: 'Field', flex: 1 },
      {
        field: 'initialData',
        headerName: 'Initial',
        flex: 1,
        renderCell: ({ row, value }) => {
          const formattedValue = formatDisplayValue(value, row.fields);
          return <span>{formattedValue}</span>;
        },
      },
      {
        field: 'historyData',
        headerName: formatTableDate(startDate),
        flex: 1,
        renderCell: ({ row, value }) => {
          const formattedValue = formatDisplayValue(value, row.fields);
          return <span style={{ color: 'red' }}>{formattedValue}</span>;
        },
      },
      {
        field: 'latestData',
        headerName: 'Latest',
        flex: 1,
        renderCell: ({ row, value }) => {
          const formattedValue = formatDisplayValue(value, row.fields);
          const prev = Number(row.historyData);
          const latest = Number(value);

          if (!isNaN(prev) && !isNaN(latest) && row.fields !== 'IMO') {
            const Icon =
              latest > prev
                ? ArrowUpward
                : latest < prev
                  ? ArrowDownward
                  : undefined;
            const iconColor =
              latest > prev ? 'green' : latest < prev ? 'red' : 'inherit';
            return (
              <Stack direction='row' alignItems='center' gap={0.5}>
                {Icon && <Icon style={{ color: iconColor, fontSize: 18 }} />}
                <span style={{ color: 'green' }}>{formattedValue}</span>
              </Stack>
            );
          }
          return (
            <Stack direction='row' alignItems='center' gap={0.5}>
              <ArrowRightIcon color='primary' style={{ fontSize: 18 }} />
              <span style={{ color: 'green' }}>{formattedValue}</span>
            </Stack>
          );
        },
      },
    ],
    [startDate]
  );

  if (!voyageHistory) return null;

  const {
    voyageFieldChanges,
    shipmentsCreated,
    shipmentsUpdated,
    shipmentsDeleted,
  } = voyageHistory;

  const voyageRows =
    voyageFieldChanges?.map((c, i) => ({
      id: i,
      fields: mapLabelToField(c.field),
      initialData: getCellDisplayValue(c.initialValue, c.field),
      historyData: getCellDisplayValue(c.previousValue, c.field),
      latestData: getCellDisplayValue(c.currentValue, c.field),
    })) ?? [];

  const updatedShipments = getShipmentsDisplayData(shipmentsUpdated);
  const addedShipments = getShipmentsDisplayData(shipmentsCreated);
  const removedShipments = getShipmentsDisplayData(shipmentsDeleted);

  const countUniqueMarkets = (shipments: ShipmentChange[]) => shipments.length;

  const updatedCount = countUniqueMarkets(shipmentsUpdated);
  const addedCount = countUniqueMarkets(shipmentsCreated);
  const removedCount = countUniqueMarkets(shipmentsDeleted);
  const noChanges =
    !voyageRows.length && !updatedCount && !addedCount && !removedCount;

  const GRID_CONFIG = [
    {
      title: 'Updated',
      icon: ChangeCircleOutlined,
      data: updatedShipments,
      color: 'info',
      count: updatedCount,
      type: 'UPDATED',
      isOpen: isUpdatedShipmentSectionOpen,
      setIsOpen: setIsUpdatedShipmentSectionOpen,
    },
    {
      title: 'Added',
      icon: AddCircleOutline,
      data: addedShipments,
      color: 'success',
      count: addedCount,
      type: 'ADDED',
      isOpen: isAddedShipmentSectionOpen,
      setIsOpen: setIsAddedShipmentSectionOpen,
    },
    {
      title: 'Removed',
      icon: RemoveCircleOutline,
      data: removedShipments,
      color: 'error',
      count: removedCount,
      type: 'DELETED',
      isOpen: isRemovedShipmentSectionOpen,
      setIsOpen: setIsRemovedShipmentSectionOpen,
    },
  ];

  const onModalClose = () => {
    onClose();
  };

  const handleDateChange = (date: string) => {
    setStartDate(date);
    localStorage.setItem('voyage-history-date', date);
  };

  return (
    <MBModal
      isLoading={loading}
      title={`Compare Voyage History - ${selectedVoyage?.voyageNumber || '-'}`}
      open={open}
      onClose={onModalClose}
      maxWidth='lg'
    >
      <VoyageHeader
        selectedVoyage={selectedVoyage}
        startDate={startDate}
        setStartDate={handleDateChange}
      />
      {loading ? null : (
        <Box sx={{ overflowY: 'auto' }}>
          <Stack gap={2}>
            {noChanges ? (
              <Box p={4} textAlign='center'>
                {error ? (
                  <Box p={4} textAlign='center'>
                    <Typography color='error'>{error}</Typography>
                  </Box>
                ) : (
                  <Typography color='text.secondary'>
                    No voyage or shipment changes found for this date.
                  </Typography>
                )}
              </Box>
            ) : (
              <>
                {/* Voyages */}
                <Accordion
                  slotProps={{
                    transition: {
                      timeout: 0,
                    },
                  }}
                  defaultExpanded={voyageRows.length > 0}
                  expanded={voyageRows.length > 0 && isUpdatedVoyageSectionOpen}
                  onChange={(_, expanded) =>
                    setIsUpdatedVoyageSectionOpen(expanded)
                  }
                >
                  <AccordionSummary
                    expandIcon={<ExpandMore />}
                    disabled={voyageRows.length === 0}
                  >
                    <Stack direction='row' alignItems='center' spacing={1}>
                      <ChangeCircleOutlined color='primary' />
                      <Typography variant='subtitle1'>
                        Updated Voyage Fields ({voyageRows.length})
                      </Typography>
                    </Stack>
                  </AccordionSummary>
                  <Divider />
                  <AccordionDetails>
                    <ChangesDataGrid
                      rows={voyageRows}
                      columns={voyageColumns}
                    />
                  </AccordionDetails>
                </Accordion>

                {/* Shipments */}
                {GRID_CONFIG.map(
                  ({
                    title,
                    icon: Icon,
                    data,
                    color,
                    count,
                    type,
                    isOpen,
                    setIsOpen,
                  }) => (
                    <Accordion
                      key={title}
                      expanded={isOpen && count > 0}
                      onChange={(_, expanded) => setIsOpen(expanded)}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMore />}
                        disabled={count === 0}
                      >
                        <Stack direction='row' alignItems='center' spacing={1}>
                          <Icon color={color as any} />
                          <Typography variant='subtitle1'>
                            {title} Shipments ({count})
                          </Typography>
                        </Stack>
                      </AccordionSummary>
                      <Divider />
                      <AccordionDetails>
                        {/* Updated, list stacked full width Added/Removed, responsive grid side by side */}
                        {type === 'UPDATED' ? (
                          <Stack spacing={2}>
                            {Object.entries(data).map(([key, rows]) => {
                              const [countryCode, id, ...rest] = key.split('-');
                              const marketName = rest.slice(0, -1).join('-');
                              const createdAt = rows[0]?.createdAt;
                              return (
                                <ShipmentChangeSection
                                  key={key}
                                  countryCode={countryCode}
                                  marketId={id}
                                  changeType={type as ShipmentChangeType}
                                  createdAt={createdAt}
                                  title={marketName}
                                  rows={rows}
                                  columns={getShipmentColumns(
                                    type as ShipmentChangeType,
                                    startDate,
                                    theme
                                  )}
                                />
                              );
                            })}
                          </Stack>
                        ) : (
                          <Grid container spacing={2}>
                            {Object.entries(data).map(([key, rows]) => {
                              const [countryCode, id, ...rest] = key.split('-');
                              const marketName = rest.slice(0, -1).join('-');
                              const createdAt = rows[0]?.createdAt;

                              return (
                                <Grid item xs={12} key={key}>
                                  <ShipmentChangeSection
                                    key={key}
                                    countryCode={countryCode}
                                    marketId={id}
                                    createdAt={createdAt}
                                    changeType={type as ShipmentChangeType}
                                    title={marketName}
                                    rows={rows}
                                    columns={getShipmentColumns(
                                      type as ShipmentChangeType,
                                      startDate,
                                      theme
                                    )}
                                  />
                                </Grid>
                              );
                            })}
                          </Grid>
                        )}
                      </AccordionDetails>
                    </Accordion>
                  )
                )}
              </>
            )}
          </Stack>
        </Box>
      )}
    </MBModal>
  );
};

export default VoyageHistoryModal;
